import { AxiosRequestConfig, AxiosResponse } from 'axios'
import {
  EligibilityResponse,
  SaveUserRegistrationResponse,
  UserValidateRequest,
  UserValidateResponse
} from 'redux/registration/typings'
import apiRequestor from 'services/apiRequestor'
import {
  PartialSaveUserRegistrationRequest,
  RegistrationRequest,
  SaveUserRegistrationPayload,
  UpdatePrivilegeRequest
} from 'typings/registration'
import apiEndpoints from 'const/endPoint'
import {
  getAuthInfo,
  getHeaderParams,
  getLoginHeaderParams,
  getPartialSaveRegistrationParams,
  getRegistrationEligibleParams,
  getRegistrationHeaderParams,
  getSaveRegistrationParams,
  getUpdatePrivilegeParams
} from 'helpers/requestParams'
import config from 'const/config'
import { readBlackBox } from 'helpers/windowUtility'
import { Iovation } from 'typings/apiRequest'

const { APIGEE_CLIENT_ID } = config

/**
 * Params to the save first time login service method. Mostly a direct mapping
 * of the request body.
 */
export interface SaveFirstTimeLoginParams {
  accessToken: string
  accountNumber: string
  phoneNumber: string
  email: string
  sessionId: string
  password: string
  dob: string
  iovation: Iovation
  firstTimeLogin: string
}

export interface SaveFirstTimeLoginResponse {
  authCode: string
}

const registrationAPI = {
  getRegistrationEligibility: async (
    payload: RegistrationRequest
  ): Promise<EligibilityResponse> => {
    const headerParams = getLoginHeaderParams()

    const requestConfig: AxiosRequestConfig = {
      url: `${apiEndpoints.registrationEligibility}?client_id=${APIGEE_CLIENT_ID}`,
      method: 'POST',
      headers: { ...headerParams },
      data: getRegistrationEligibleParams(payload)
    }

    const response = await apiRequestor<EligibilityResponse>({
      requestConfig,
      hasAccountInfo: false
    })

    return response.data
  },
  checkUserAvailability: async (
    payload: UserValidateRequest
  ): Promise<UserValidateResponse> => {
    const { accessToken, sessionId, userId } = payload
    const headerParams = getHeaderParams(accessToken)

    const requestConfig: AxiosRequestConfig = {
      url: `${apiEndpoints.checkUserExist}`,
      method: 'POST',
      headers: { ...headerParams },
      data: {
        sessionId,
        userId
      }
    }

    const response = await apiRequestor<UserValidateResponse>({
      requestConfig,
      hasAccountInfo: false
    })

    return response.data
  },
  saveUserRegistration: async (
    payload: SaveUserRegistrationPayload
  ): Promise<SaveUserRegistrationResponse> => {
    const { accessToken } = payload

    const headerParams = getHeaderParams(accessToken, '', true)

    const saveRegistrationPayload = {
      ...payload,
      iovation: readBlackBox()
    }

    const requestConfig: AxiosRequestConfig = {
      url: `${apiEndpoints.saveRegistration}`,
      method: 'POST',
      headers: { ...headerParams },
      data: getSaveRegistrationParams(saveRegistrationPayload)
    }

    const response = await apiRequestor<SaveUserRegistrationResponse>({
      requestConfig,
      hasAccountInfo: false
    })

    return response.data
  },
  // First Time Login Implementation
  saveFirstTimeLogin: async ({
    accessToken,
    accountNumber,
    phoneNumber,
    email,
    sessionId,
    password,
    dob,
    iovation,
    firstTimeLogin
  }: SaveFirstTimeLoginParams): Promise<
    AxiosResponse<SaveFirstTimeLoginResponse | null>
  > => {
    const headerParams = getHeaderParams(accessToken, '', true)

    const typeQueryParam =
      firstTimeLogin === 'C' || firstTimeLogin === 'R'
        ? 'registration-complete'
        : 'online-user'

    const requestConfig: AxiosRequestConfig = {
      url: `${apiEndpoints.saveFirstTimeLogin}?type=${typeQueryParam}`,
      method: 'PATCH',
      headers: { ...headerParams },
      data: {
        accountNumber,
        businessInfo: {
          email,
          phoneNumber
        },
        password,
        motherDateOfBirth: dob,
        sessionId,
        authInfo: {
          ...getAuthInfo(),
          scope: 'login'
        },
        iovation
      }
    }

    return apiRequestor<SaveFirstTimeLoginResponse | null>({
      requestConfig,
      hasAccountInfo: false
    })
  },
  partialSaveUserRegistration: async (
    payload: PartialSaveUserRegistrationRequest
  ): Promise<unknown> => {
    const { accessToken, accountType } = payload

    const headerParams = getRegistrationHeaderParams(accessToken, accountType)

    const requestConfig: AxiosRequestConfig = {
      url: `${apiEndpoints.saveRegistration}`,
      method: 'POST',
      headers: { ...headerParams },
      data: getPartialSaveRegistrationParams(payload)
    }

    const response = await apiRequestor<unknown>({
      requestConfig,
      hasAccountInfo: false
    })

    return response.data
  },
  updateChildAccountPrivilege: async (
    payload: UpdatePrivilegeRequest
  ): Promise<unknown> => {
    const { accessToken } = payload

    const headerParams = getHeaderParams(accessToken)

    const requestConfig: AxiosRequestConfig = {
      url: `${apiEndpoints.registrationPrivilege}`,
      method: 'POST',
      headers: { ...headerParams },
      data: getUpdatePrivilegeParams(payload)
    }

    const response = await apiRequestor<unknown>({
      requestConfig,
      hasAccountInfo: false
    })

    return response.data
  }
}

export default registrationAPI
